import classnames from 'classnames'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'

import { CategoryPageTemplateQuery } from '../../../graphql/gatsby'
import useMediaQuery from '../../../hooks/useMediaQuery'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { GetProductsQueryResults, UseProducts } from '../../../lib/products'
import ProductList from '../../products/lists/ProductList'
import { AppliedFilters } from './AppliedFilters'
import FiltersContainer from './filters/FiltersContainer'
import MobileFilters from './filters/MobileFilters'
import LoadMoreProducts from './LoadMoreProducts'
import LoadPreviousProducts from './LoadPreviousProducts'
import ProductCount, { ProductCountProps } from './ProductCount'
import ProductLabelStatusLegend from './ProductLabelStatusLegend'
import SortedByRelevance from './SortedByRelevance'
import { SortProductsDropdown } from './SortProductsDropdown'

const HeadingRow = styled.div`
  display: flex;
  align-content: flex-start;
  justify-content: space-between;
  flex-basis: 100%;
  flex-wrap: wrap;
  padding: 25px 15px 0;

  @media (min-width: 992px) {
    flex: 1 0 calc(100% - 75px);
    padding: 16px 80px 30px 40px;
  }
`

const Layout = styled.div`
  display: flex;
  flex-direction: row;
`

const ProductListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ModeLine = styled.div`
  display: flex;
  flex-basis: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool400};

  h2 {
    padding: 25px 15px 0;
    @media (min-width: 992px) {
      padding: 16px 80px 0 45px;
    }
  }
`

const MobileMediaQuery: FC<{ renderOnMatch: boolean }> = ({ children, renderOnMatch }) => {
  const isNarrow = useMediaQuery({ maxWidth: '991px' })
  return (renderOnMatch && isNarrow) || (!renderOnMatch && !isNarrow) ? <>{children}</> : null
}

export interface ProductsProps {
  className?: string
  pagination: UseProducts['pagination']
  listing: UseProducts['listing']
  loading: boolean
  magentoCategory?: CategoryPageTemplateQuery['magentoCategory']
  productCount: ProductCountProps['productCount']
  productCountText?: ProductCountProps['productCountText']
  products?: GetProductsQueryResults | null
  showHeadingAndBreadcrumbs?: boolean
  showLabelStatusIndicators?: boolean
  showSortedByRelevance?: boolean
}

const Products: FC<ProductsProps> = ({
  className,
  pagination,
  listing,
  loading,
  magentoCategory,
  productCount,
  productCountText,
  products,
  showHeadingAndBreadcrumbs,
  showLabelStatusIndicators,
  showSortedByRelevance = false,
}) => {
  const dispensary = useDispensaryContext()
  const activeFilters = useMemo(() => {
    const {
      category = [],
      ingredient = [],
      ailment = [],
      labelStatus = [],
      nameRange = [],
      productForm = [],
      specialty = [],
    } = listing.state.filters

    return [
      ...category,
      ...ingredient,
      ...ailment,
      ...labelStatus,
      ...nameRange,
      ...productForm,
      ...specialty,
    ].filter(Boolean)
  }, [listing.state.filters])

  return (
    <div className={className}>
      {showHeadingAndBreadcrumbs && (
        <HeadingRow>
          <MobileMediaQuery renderOnMatch={true}>
            <h2>{magentoCategory?.name || 'Products'}</h2>
          </MobileMediaQuery>
          {showLabelStatusIndicators && <ProductLabelStatusLegend />}
        </HeadingRow>
      )}
      <ModeLine>
        <MobileMediaQuery renderOnMatch={true}>
          <MobileFilters
            aggregations={products?.aggregations}
            listing={listing}
            loading={loading}
            activeFilters={activeFilters}
          />
        </MobileMediaQuery>
        <MobileMediaQuery renderOnMatch={false}>
          <h2>{magentoCategory?.name || 'Products'}</h2>
        </MobileMediaQuery>
        <ProductCount
          loading={loading}
          productCount={productCount}
          productCountText={productCountText || 'PRODUCT'}
        />
        {showSortedByRelevance ? <SortedByRelevance /> : <SortProductsDropdown listing={listing} />}
      </ModeLine>
      <Layout>
        <MobileMediaQuery renderOnMatch={false}>
          <FiltersContainer
            aggregations={products?.aggregations}
            listing={listing}
            loading={loading}
          />
        </MobileMediaQuery>
        <ProductListWrapper>
          <AppliedFilters listing={listing} activeFilters={activeFilters} />
          <div className={classnames('item-boxes-wrapper')}>
            <LoadPreviousProducts loading={loading} pagination={pagination} products={products} />
            <ProductList
              productsPagination={pagination}
              paginationState={listing.state.pagination}
              products={products?.results}
              analyticsEventAppContext={dispensary ? 'dispensary_plp' : 'plp'}
            />
            <LoadMoreProducts loading={loading} pagination={pagination} products={products} />
          </div>
        </ProductListWrapper>
      </Layout>
    </div>
  )
}

Products.defaultProps = {
  className: '',
  products: null,
  productCount: {
    all: 0,
    current: 0,
  },
  productCountText: 'RESULT',
  showHeadingAndBreadcrumbs: true,
}

const StyledProducts = styled(Products)(({ theme, magentoCategory }) => ({
  '--category-background-color':
    magentoCategory?.url_path === 'women-s-health'
      ? '#f1dfed'
      : magentoCategory?.url_path === 'designs-for-beauty'
        ? '#EFD9C2'
        : undefined,
  backgroundColor: `var(--category-background-color, ${theme.colors.grayscale.white})`,
  display: 'flex',
  flexWrap: 'wrap',
  '.item-boxes-wrapper': {
    backgroundColor: `var(--category-background-color, ${theme.colors.grayscale.white})`,
    flexBasis: '100%',
    width: '100%',
    paddingTop: '0',
  },
  '@media(min-width: 992px)': {
    backgroundColor: `var(--category-background-color, ${theme.colors.coolGray.cool150})`,
    display: 'flex',
    padding: '0',
    '.item-boxes-wrapper': {
      display: 'flex',
      backgroundColor: `var(--category-background-color, ${theme.colors.coolGray.cool150})`,
      flex: '0 1 auto',
      flexDirection: 'column',
      // avoid stretching item boxes to fill height of filters container
      alignSelf: 'flex-start',
      paddingRight: '80px',
      paddingTop: '0',
      '&.hideFilters': {
        paddingLeft: '40px',
      },
    },
  },
}))

export default StyledProducts
